import React from "react";
import api, { apiError } from "@/lib/api";
import { PageHeader, Card, SkeletonRows, EmptyState } from "@/components/dash/ui";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { toast } from "sonner";
import { formatDistanceToNow } from "date-fns";
import { Mail, CheckCircle2, XCircle, Activity, Send, RefreshCw } from "lucide-react";

const STATUS_FILTERS = ["all", "SENT", "FAILED"];
const TEMPLATE_OPTIONS = [
  { key: "registration_otp", label: "Registration OTP" },
  { key: "password_reset", label: "Password Reset" },
  { key: "password_reset_confirmation", label: "Password Reset Confirmation" },
  { key: "welcome", label: "Welcome" },
];

function StatusPill({ status }) {
  const map = {
    SENT: "bg-emerald-50 text-emerald-700 border-emerald-200",
    FAILED: "bg-rose-50 text-rose-700 border-rose-200",
  };
  return <span className={`inline-flex items-center gap-1 rounded-full border px-2 py-0.5 text-xs font-semibold ${map[status] || "bg-slate-50 text-slate-600 border-slate-200"}`} data-testid={`email-status-${status}`}>{status}</span>;
}

function HealthCard() {
  const [health, setHealth] = React.useState(null);
  const load = () => { setHealth(null); api.get("/admin/email/health").then((r) => setHealth(r.data)).catch(() => setHealth(false)); };
  React.useEffect(() => { load(); }, []);
  const healthy = health?.status === "healthy";
  return (
    <Card>
      <div className="flex items-center justify-between mb-3">
        <h3 className="font-semibold text-slate-800 flex items-center gap-2"><Activity className="h-4 w-4 text-vs-royal" />Email Service Health</h3>
        <Button variant="outline" size="sm" onClick={load} data-testid="email-health-refresh"><RefreshCw className="h-3.5 w-3.5 mr-1" />Check</Button>
      </div>
      {health === null ? <SkeletonRows /> : health === false ? <p className="text-sm text-rose-600">Unable to run health check.</p> : (
        <div className="space-y-2" data-testid="email-health-result">
          <div className="flex items-center gap-2">
            <span className={`h-2.5 w-2.5 rounded-full ${healthy ? "bg-emerald-500" : "bg-amber-500"}`} />
            <span className={`text-sm font-semibold ${healthy ? "text-emerald-700" : "text-amber-700"}`}>{healthy ? "Healthy" : "Configuration Required"}</span>
          </div>
          <div className="grid sm:grid-cols-2 gap-x-6 gap-y-1 text-sm">
            <Row k="Provider" v={health.checks?.provider} />
            <Row k="API Key" v={health.checks?.api_key_configured ? "Configured" : "Missing"} />
            <Row k="From Name" v={health.checks?.from_name} />
            <Row k="From Email" v={health.checks?.from_email} />
            <Row k="App Base URL" v={health.checks?.app_base_url} />
            <Row k="Connectivity" v={health.checks?.connectivity ? "OK" : "Failed"} />
          </div>
          {health.detail && <p className="text-xs text-amber-600">{health.detail}</p>}
        </div>
      )}
    </Card>
  );
}

const Row = ({ k, v }) => (<div className="flex justify-between gap-3 py-0.5 border-b border-slate-50 min-w-0"><span className="text-slate-500 shrink-0">{k}</span><span className="font-medium text-slate-800 text-right break-all min-w-0">{String(v ?? "—")}</span></div>);

function TestEmailDialog({ open, onClose }) {
  const [to, setTo] = React.useState("");
  const [template, setTemplate] = React.useState("registration_otp");
  const [sending, setSending] = React.useState(false);
  const send = async () => {
    setSending(true);
    try {
      const { data } = await api.post("/admin/email/test", { to, template });
      toast.success(data.message || "Test email accepted by provider");
      onClose(true);
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); }
    finally { setSending(false); }
  };
  return (
    <Dialog open={open} onOpenChange={() => onClose(false)}>
      <DialogContent className="max-w-md">
        <DialogHeader><DialogTitle>Send Test Email</DialogTitle>
          <DialogDescription>Verifies provider configuration by sending a real email through the managed provider.</DialogDescription></DialogHeader>
        <div className="space-y-3 mt-2">
          <div><Label>Recipient</Label><Input type="email" value={to} onChange={(e) => setTo(e.target.value)} placeholder="name@example.com" data-testid="email-test-to" className="mt-1" /></div>
          <div><Label>Template</Label>
            <select value={template} onChange={(e) => setTemplate(e.target.value)} data-testid="email-test-template"
              className="mt-1 w-full rounded-lg border border-slate-200 px-3 py-2 text-sm text-slate-800 focus:border-vs-royal outline-none">
              {TEMPLATE_OPTIONS.map((t) => <option key={t.key} value={t.key}>{t.label}</option>)}
            </select></div>
          <div className="flex justify-end gap-2 pt-1">
            <Button variant="outline" onClick={() => onClose(false)}>Cancel</Button>
            <Button onClick={send} disabled={sending || !to} className="bg-vs-royal text-white" data-testid="email-test-send"><Send className="h-3.5 w-3.5 mr-1" />{sending ? "Sending…" : "Send Test"}</Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}

export default function EmailCenter() {
  const [events, setEvents] = React.useState(null);
  const [stats, setStats] = React.useState(null);
  const [status, setStatus] = React.useState("all");
  const [dialog, setDialog] = React.useState(false);

  const load = React.useCallback(() => {
    setEvents(null);
    api.get("/admin/email/events", { params: { status, page_size: 50 } }).then((r) => setEvents(r.data.items)).catch(() => setEvents([]));
    api.get("/admin/email/stats").then((r) => setStats(r.data)).catch(() => {});
  }, [status]);
  React.useEffect(() => { load(); }, [load]);

  return (
    <div>
      <PageHeader title="Email Center" description="Transactional email delivery log, provider health and test sends." breadcrumb="Communications / Email Center">
        <Button onClick={() => setDialog(true)} className="bg-vs-royal text-white" data-testid="email-test-open"><Send className="h-4 w-4 mr-1.5" />Send Test Email</Button>
      </PageHeader>

      <div className="grid sm:grid-cols-3 gap-4 mb-6 min-w-0">
        <Card className="flex items-center gap-3"><div className="h-10 w-10 rounded-lg bg-vs-light flex items-center justify-center"><Mail className="h-5 w-5 text-vs-royal" /></div>
          <div><div className="text-2xl font-bold text-slate-800" data-testid="email-stat-total">{stats?.total ?? "—"}</div><div className="text-xs text-slate-500">Total Emails</div></div></Card>
        <Card className="flex items-center gap-3"><div className="h-10 w-10 rounded-lg bg-emerald-50 flex items-center justify-center"><CheckCircle2 className="h-5 w-5 text-emerald-600" /></div>
          <div><div className="text-2xl font-bold text-slate-800" data-testid="email-stat-sent">{stats?.sent ?? "—"}</div><div className="text-xs text-slate-500">Sent</div></div></Card>
        <Card className="flex items-center gap-3"><div className="h-10 w-10 rounded-lg bg-rose-50 flex items-center justify-center"><XCircle className="h-5 w-5 text-rose-600" /></div>
          <div><div className="text-2xl font-bold text-slate-800" data-testid="email-stat-failed">{stats?.failed ?? "—"}</div><div className="text-xs text-slate-500">Failed</div></div></Card>
      </div>

      <HealthCard />

      <Card className="mt-6">
        <div className="flex items-center justify-between mb-4 flex-wrap gap-2">
          <h3 className="font-semibold text-slate-800">Delivery Log</h3>
          <div className="flex items-center gap-2" data-testid="email-status-filter">
            {STATUS_FILTERS.map((s) => (
              <button key={s} onClick={() => setStatus(s)} data-testid={`email-filter-${s}`}
                className={`rounded-lg border px-3 py-1.5 text-xs font-semibold transition-colors ${status === s ? "border-vs-royal bg-vs-light text-vs-royal" : "border-slate-200 bg-white text-slate-500"}`}>
                {s === "all" ? "All" : s}
              </button>
            ))}
            <Button variant="outline" size="sm" onClick={load} data-testid="email-log-refresh"><RefreshCw className="h-3.5 w-3.5" /></Button>
          </div>
        </div>
        {events === null ? <SkeletonRows /> : events.length === 0 ? <EmptyState icon="Mail" title="No email events yet" /> : (
          <div className="overflow-x-auto min-w-0 -mx-5 px-5">
            <table className="w-full min-w-[720px] text-sm" data-testid="email-events-table">
              <thead><tr className="text-left text-xs uppercase tracking-wider text-slate-400 border-b border-slate-100">
                <th className="py-2.5 font-semibold">Recipient</th><th className="py-2.5 font-semibold">Template</th>
                <th className="py-2.5 font-semibold">Status</th><th className="py-2.5 font-semibold">Provider Msg ID</th>
                <th className="py-2.5 font-semibold">When</th><th className="py-2.5 font-semibold">Detail</th>
              </tr></thead>
              <tbody>
                {events.map((e) => (
                  <tr key={e.id} className="border-b border-slate-50" data-testid={`email-event-row`}>
                    <td className="py-3 font-medium text-slate-800">{e.email}</td>
                    <td className="py-3 text-slate-600">{e.template}</td>
                    <td className="py-3"><StatusPill status={e.status} /></td>
                    <td className="py-3"><code className="text-xs text-slate-400">{e.provider_message_id ? e.provider_message_id.slice(0, 16) + "…" : "—"}</code></td>
                    <td className="py-3 text-slate-500 text-xs">{e.created_at ? formatDistanceToNow(new Date(e.created_at), { addSuffix: true }) : "—"}</td>
                    <td className="py-3 text-xs">{e.error_reason ? <span className="text-rose-600">{e.error_reason}</span> : e.cc_dropped?.length ? <span className="text-amber-600">CC dropped: {e.cc_dropped.join(", ")}</span> : <span className="text-slate-300">—</span>}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Card>

      <p className="text-[11px] text-slate-400 mt-3">OTP codes and reset tokens are never stored or displayed here — only delivery metadata.</p>

      {dialog && <TestEmailDialog open={dialog} onClose={(sent) => { setDialog(false); if (sent) load(); }} />}
    </div>
  );
}
